import React, { useState } from 'react';
import { useParams } from 'react-router';
import { useQuery } from '@apollo/client';
import Lozenge from '@atlaskit/lozenge';
import capitalize from 'lodash/capitalize';
import get from 'lodash/get';
import Button from '@atlaskit/button';
import ShortcutIcon from '@atlaskit/icon/glyph/shortcut';
import { AnalyticsEventSource } from '@atlassian/temp-analytics';
import { LoadingIndicator } from '@atlassian/temp-loading-indicator';
import { txaPushPaymentStatus } from '@atlassian/cssxp-types-billing';
import { PromotionPartnerTier } from '@atlassian/cssxp-ccp-types';
import { PageBackground, PageBackgroundV2 } from '@css-xp/page-background';
import { CreditDetailsPageLoader } from '@css-xp/ppc-page-loaders';
import { handleSupportLinkClick, getCreditUsageUiUrlViaIdentity } from '@css-xp/ppc-navigation';
import { Background } from '@css-xp/page-background/src/background-image';
import {
  FetchTermDetailsPpcQuery,
  FetchTermDetailsPpcQueryVariables,
  FetchAssociatedBillingAccountsQuery,
  FetchAssociatedBillingAccountsQueryVariables,
  TermPaymentTypes,
  TransactionAccount,
  PaymentMethodType,
} from '@css-xp/graphtypes/src/generated-types-new';
import {
  AppContent,
  AppPage,
  ErrorComponents,
  formatTermPaymentName,
  InfoPanel,
  useApplicationContext,
  useFeatureToggle,
} from '@css-xp/base';
import { DarkButton } from '@css-xp/ppc-dark-button';
import { withErrorWrapper } from '@css-xp/ppc-wrapper';
import {
  ScreenEventAnalytics,
  Net30StatusBanner,
  useNet30ApplicationStatus,
  Currency,
  LimitCurrencyDropdown,
  getCurrencyByCountry,
  fetchAssociatedBillingAccounts,
} from '@css-xp/ppc-common';
import { ENABLE_DESIGN_ITERATION_1, STATSIG_FEATURE_GATES, routePaths } from '@css-xp/ppc-constants';

import { CreditDetailsSummarySection } from './components/credit-details-summary-section/credit-details-summary-section';
import { CreditDetailsTabs } from './components/credit-details-tabs/credit-details-tabs';
import { fetchTermDetailsPPC } from './queries/fetch-term-details-ppc';
import './credit-details-page.scss';

export const CreditDetailsPage = () => {
  const { txaId } = useParams<{ txaId: string }>();
  const [isEnabled] = useFeatureToggle();
  const data = useApplicationContext();
  const selectedPartnerAccountData = data?.partnerAccounts?.find(
    ({ transactionAccountId }: { transactionAccountId: string }) => transactionAccountId === txaId
  );
  const initialCurrency = getCurrencyByCountry(selectedPartnerAccountData?.billTo?.country) || Currency.USD;
  const [currency, setCurrency] = useState<string>(initialCurrency.toUpperCase());

  const isDesignIteration1Enabled = isEnabled(ENABLE_DESIGN_ITERATION_1);
  const isMultiCurrencyChangesEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_MULTI_CURRENCY_CHANGES);

  const sfdcId = selectedPartnerAccountData?.id;

  const { data: termPaymentData, loading, error } = useQuery<
    FetchTermDetailsPpcQuery,
    FetchTermDetailsPpcQueryVariables
  >(fetchTermDetailsPPC, {
    variables: {
      id: txaId,
    },
    skip: !txaId,
    fetchPolicy: 'cache-and-network',
  });

  const associatedBillingAccountsResponse = useQuery<
    FetchAssociatedBillingAccountsQuery,
    FetchAssociatedBillingAccountsQueryVariables
  >(fetchAssociatedBillingAccounts, {
    variables: { id: txaId, currency },
    fetchPolicy: 'no-cache',
    skip: !isMultiCurrencyChangesEnabled && !txaId,
  });

  const transactionAccountData = termPaymentData?.transactionAccount;
  const associatedBillingAccounts = associatedBillingAccountsResponse?.data?.transactionAccount?.billingAccountsList;
  const billingAccountsList = isMultiCurrencyChangesEnabled
    ? associatedBillingAccounts
    : transactionAccountData?.billingAccountsList;
  const termPaymentMethod = billingAccountsList?.paymentMethod;
  const { isInProgress, openTicketNumber } = useNet30ApplicationStatus({ txaId, paymentMethod: termPaymentMethod });

  if (loading) {
    return isDesignIteration1Enabled ? <CreditDetailsPageLoader /> : <LoadingIndicator />;
  }

  if (error || associatedBillingAccountsResponse?.error) {
    const GenericError = withErrorWrapper(ErrorComponents.GenericErrorPage);
    return <GenericError error={error || associatedBillingAccountsResponse?.error} />;
  }

  const deferredPaymentMethod = termPaymentData?.transactionAccount?.availablePaymentMethods?.nodes?.filter(
    (paymentMethod) => paymentMethod?.type === PaymentMethodType?.DEFERRED
  );

  const hasPayByStatement =
    deferredPaymentMethod && deferredPaymentMethod?.length > 0
      ? get(deferredPaymentMethod[0], 'terms.payOnStatement')
      : false;

  const invoiceDueDays =
    deferredPaymentMethod && deferredPaymentMethod.length > 0
      ? get(deferredPaymentMethod[0], 'terms.invoiceDueDays')
      : 0;

  const isNet14PaymentMethod = termPaymentMethod === TermPaymentTypes.NET14 || invoiceDueDays === 14;
  const isNet30PaymentMethod = termPaymentMethod === TermPaymentTypes.NET30 || invoiceDueDays === 30;

  const currentTermAccountStatus = billingAccountsList?.status;
  const isIneligible = currentTermAccountStatus === txaPushPaymentStatus.INELIGIBLE;

  const getPageHeading = (paymentMethod: string) => {
    return (
      <div className="page-header">
        {hasPayByStatement
          ? 'On account payment terms'
          : paymentMethod
          ? `Pay later by invoice (${formatTermPaymentName(paymentMethod)})`
          : 'Pay later by invoice'}
        {isIneligible && (
          <span className="page-sub-header">
            <Lozenge appearance="removed">DEACTIVATED</Lozenge>
          </span>
        )}
      </div>
    );
  };

  const getDeactivationPanelText = () => {
    let paymentTermsName = '';
    if (hasPayByStatement) {
      paymentTermsName = isDesignIteration1Enabled ? 'on account' : 'On account';
    } else if (termPaymentMethod === TermPaymentTypes.NET30) {
      paymentTermsName = 'Net-30';
    } else if (termPaymentMethod === TermPaymentTypes.NET14) {
      paymentTermsName = 'Net-14';
    }

    if (isDesignIteration1Enabled) {
      const deactivatedMessage = (
        <>
          If you didn't expect {paymentTermsName} payment terms to be deactivated, contact&nbsp;
          <a
            href="javascript:void(0);"
            onClick={() => {
              handleSupportLinkClick();
            }}
          >
            Partner Service Desk
          </a>
          .
        </>
      );

      return deactivatedMessage;
    }

    const deactivationMessage = 'payment terms have been deactivated for this billing account.';

    return `${paymentTermsName} ${deactivationMessage}`;
  };

  const showMoreActions = () => {
    const moreActionButtons = [];
    const commonExternalIcon = <ShortcutIcon label="open-in-new-tab-icon" size="small" />;
    if (!hasPayByStatement) {
      const applyText = 'Apply for higher payment terms';
      const applyLinkHandler = () => {
        window.open(getCreditUsageUiUrlViaIdentity({ transactionAccountId: txaId, email: data.email }), '_blank');
      };
      const className = 'show-more-action-credit-details-container';
      const testId = 'apply-for-higher-payment-terms-btn';
      const button = isDesignIteration1Enabled ? (
        <Button onClick={applyLinkHandler} testId={testId} className={className} iconAfter={commonExternalIcon}>
          {applyText}
        </Button>
      ) : (
        <DarkButton
          onClick={applyLinkHandler}
          testId={testId}
          className={className}
          iconAfter={commonExternalIcon}
          appearance="primary"
        >
          {applyText}
        </DarkButton>
      );

      moreActionButtons.push(button);
    }

    return isMultiCurrencyChangesEnabled ? (
      <div className="term-details-actions">
        <div>{moreActionButtons}</div>
        <div className="currency-dropdown">
          <LimitCurrencyDropdown
            currency={currency}
            setCurrency={setCurrency}
            label="View credits in"
            showLabel
            isDesignIteration1Enabled={isDesignIteration1Enabled}
            isLightTheme={isDesignIteration1Enabled}
          />
        </div>
      </div>
    ) : (
      moreActionButtons
    );
  };

  const renderAppPage = () => {
    const bannerClassNames = isDesignIteration1Enabled ? 'mb-16 pl-8' : 'mb-24 ph-16';

    return (
      <AppPage className="app-page-v2 credit-details-page">
        <ScreenEventAnalytics
          event={{
            name: AnalyticsEventSource.PARTNER_CREDIT_DETAILS_SCREEN,
            attributes: {},
          }}
        />
        <AppContent className="ppc-credit-details-page-content">
          <section>
            {isIneligible && (
              <div className={bannerClassNames}>
                <InfoPanel type="warning" message={getDeactivationPanelText()} />
              </div>
            )}
            {isInProgress && (
              <div className={bannerClassNames}>
                <Net30StatusBanner isInProgress={isInProgress} openTicketNumber={openTicketNumber} />
              </div>
            )}
            <CreditDetailsSummarySection
              transactionAccount={transactionAccountData as TransactionAccount}
              hasPayByStatement={hasPayByStatement}
              associatedBillingAccounts={associatedBillingAccounts}
              isLoading={associatedBillingAccountsResponse?.loading}
              isMultiCurrencyChangesEnabled={isMultiCurrencyChangesEnabled}
            />
          </section>
          <CreditDetailsTabs
            transactionAccountId={txaId}
            termType={termPaymentMethod as TermPaymentTypes}
            pushPaymentMethodId={deferredPaymentMethod && deferredPaymentMethod[0]?.id}
            sfdcId={sfdcId}
            isDesignIteration1Enabled={isDesignIteration1Enabled}
          />
        </AppContent>
      </AppPage>
    );
  };

  const heading = getPageHeading(isNet14PaymentMethod ? 'NET14' : isNet30PaymentMethod ? 'NET30' : '');

  if (isDesignIteration1Enabled) {
    const breadcrumbsItems = [
      {
        title: 'Home',
        href: routePaths.home,
      },
      { title: 'Credit details', href: routePaths.creditDetails },
    ];

    return (
      <PageBackgroundV2
        heading={heading}
        actions={<>{showMoreActions()}</>}
        className="credit-details-v2"
        breadcrumbsProps={{ items: breadcrumbsItems }}
      >
        {renderAppPage()}
      </PageBackgroundV2>
    );
  }

  return (
    <PageBackground
      heading={heading}
      className="ppc-credit-details-page-background"
      imageKey={Background.INVOICE_DETAILS_BACKGROUND}
      isBackButtonEnabled={true}
      showImageKey={true}
      actions={showMoreActions()}
    >
      {renderAppPage()}
    </PageBackground>
  );
};
